<template>
  <div class="vat-page">
    <div class="vat-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
        <p>Loading VAT documents...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <i class="las la-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="loadDocuments" class="retry-btn">Retry</button>
      </div>

      <div v-else class="vat-sections">
        <div class="vat-section-header">
          <h2 class="vat-section-title">VAT Statements</h2>
          <div class="status-legend">
            <span class="status-legend-item">
              <span class="status-badge status-completed"><i class="las la-check"></i></span>
              <span>Completed</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-pending"><i class="las la-clock"></i></span>
              <span>Pending</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-processing"><i class="las la-sync"></i></span>
              <span>Processing</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-error"><i class="las la-times"></i></span>
              <span>Error</span>
            </span>
          </div>
        </div>

        <div class="vat-summary-section">
          <h3 class="vat-summary-title">Summary</h3>

          <div v-if="vatSummaryLoading" class="vat-summary-state">
            <BaseSpinner size="small" />
            <span>Loading VAT summary...</span>
          </div>

          <div v-else-if="vatSummaryError" class="vat-summary-state vat-summary-error">
            <span>{{ vatSummaryError }}</span>
            <button type="button" class="vat-summary-retry" @click="loadVatSummary">Retry</button>
          </div>

          <div v-else-if="hasVatCalculation" class="vat-summary-content">
            <div v-if="vatSummaryItems.length > 0" class="vat-summary-grid">
              <div
                v-for="item in vatSummaryItems"
                :key="item.key"
                class="vat-summary-card"
                :class="{ highlight: item.highlight }"
              >
                <span class="vat-summary-label">{{ item.label }}</span>
                <strong class="vat-summary-value">{{ item.displayValue }}</strong>
              </div>
            </div>

            <div v-if="vatDataRows.length > 0" class="vat-data-table-wrapper">
              <h4 class="vat-data-title">Filing Periods</h4>
              <div class="vat-data-table-scroll">
                <table class="vat-data-table">
                  <thead>
                    <tr>
                      <th>Filing Period</th>
                      <th>Total Sales</th>
                      <th>Total Purchases</th>
                      <th>VAT Collected</th>
                      <th>VAT Paid</th>
                      <th>Net VAT Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in vatDataRows" :key="`vat-period-${index}`">
                      <td>{{ formatFilingPeriod(row.filing_period_start, row.filing_period_end) }}</td>
                      <td>{{ formatSummaryValue(row.total_sales) }}</td>
                      <td>{{ formatSummaryValue(row.total_purchases) }}</td>
                      <td>{{ formatSummaryValue(row.total_vat_collected) }}</td>
                      <td>{{ formatSummaryValue(row.total_vat_paid) }}</td>
                      <td>{{ formatSummaryValue(row.net_vat_due) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else class="vat-summary-state">
            <span>No VAT summary available yet.</span>
          </div>
        </div>

        <div class="vat-section">
          <div class="section-header">
            <div class="section-header-actions">
              <label v-if="documents.length > 0" class="select-all-checkbox">
                <input
                  type="checkbox"
                  :checked="allDocumentsSelected"
                  @change="toggleSelectAll"
                />
                <span>Select all</span>
              </label>
              <BaseButton
                stylevariant="primary"
                :cus_style="{ width: 'auto' }"
                @click="uploadDocument"
                class="upload-section-btn"
              >
                <i class="las la-plus"></i>
                Upload
              </BaseButton>
            </div>
          </div>

          <!-- Bulk Actions Bar -->
          <div v-if="selectedDocuments.length > 0" class="bulk-actions-bar">
            <div class="bulk-actions-info">
              <span>{{ selectedDocuments.length }} file{{ selectedDocuments.length !== 1 ? 's' : '' }} selected</span>
            </div>
            <div class="bulk-actions-buttons">
              <button
                @click="deleteSelectedDocuments"
                class="bulk-delete-btn"
                :disabled="deletingFiles"
                title="Delete Selected"
              >
                <BaseSpinner v-if="deletingFiles" size="small" />
                <i v-else class="las la-trash"></i>
              </button>
              <button @click="clearSelection" class="bulk-cancel-btn" title="Cancel">
                <i class="las la-times"></i>
              </button>
            </div>
          </div>

          <div v-if="documents.length === 0" class="empty-document">
            <i class="las la-file-invoice"></i>
            <p>No VAT statements uploaded</p>
          </div>
          <div v-else class="documents-list">
            <div
              v-for="doc in documents"
              :key="doc.id"
              class="document-card"
              :class="{ selected: isDocumentSelected(doc.id) }"
            >
              <div class="document-card-content">
                <label class="file-checkbox">
                  <input
                    type="checkbox"
                    :checked="isDocumentSelected(doc.id)"
                    @change="toggleDocumentSelection(doc)"
                  />
                </label>
                <span class="document-file-icon" @click="viewDocument(doc)">
                  <i :class="getFileIcon(doc)" style="color: #dc2626;"></i>
                </span>
                <span class="document-name">{{ formatName(doc.name || doc.file_name || 'VAT Statement') }}</span>
                <span class="document-date">{{ formatDate(doc.uploaded_at) }}</span>
                <span class="status-badge" :class="getStatusClass(doc.status)">
                  <i :class="getStatusIcon(doc.status)"></i>
                </span>
                <button
                  v-if="doc.status === 'completed' || doc.status === 'success'"
                  class="view-btn"
                  @click="viewDocument(doc)"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <button class="delete-btn" @click="deleteDocument(doc)" title="Delete Document">
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";
import BaseButton from "@/components/UI/BaseButton.vue";

export default {
  name: 'VatPage',
  components: { BaseSpinner, BaseButton },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      documents: [],
      selectedDocuments: [],
      deletingFiles: false,
      vatSummary: null,
      vatSummaryLoading: false,
      vatSummaryError: null
    };
  },
  computed: {
    allDocumentsSelected() {
      return this.documents.length > 0 && this.selectedDocuments.length === this.documents.length;
    },
    vatSummarySource() {
      if (!this.vatSummary || typeof this.vatSummary !== 'object') {
        return null;
      }

      return this.vatSummary.vat_summary || null;
    },
    vatDataRows() {
      return Array.isArray(this.vatSummary?.vat_data) ? this.vatSummary.vat_data : [];
    },
    hasVatCalculation() {
      return this.vatSummaryItems.length > 0 || this.vatDataRows.length > 0;
    },
    vatSummaryItems() {
      const source = this.vatSummarySource;
      if (!source || typeof source !== 'object') {
        return [];
      }

      const fieldConfig = [
        { key: 'total_vat_sales', label: 'Total VAT Sales' },
        { key: 'total_vat_purchases', label: 'Total VAT Purchases' },
        { key: 'total_vat_collected', label: 'VAT Collected' },
        { key: 'total_vat_paid', label: 'VAT Paid' },
        { key: 'net_vat_due', label: 'Net VAT Due', highlight: true }
      ];

      return fieldConfig
        .map((field) => {
          const value = source[field.key];
          if (value === undefined || value === null || value === '') {
            return null;
          }

          return {
            key: field.key,
            label: field.label,
            value,
            displayValue: this.formatSummaryValue(value),
            highlight: !!field.highlight
          };
        })
        .filter(Boolean);
    }
  },
  async mounted() {
    await Promise.all([
      this.loadDocuments(),
      this.loadVatSummary()
    ]);
  },
  methods: {
    async loadVatSummary() {
      this.vatSummaryLoading = true;
      this.vatSummaryError = null;

      try {
        this.vatSummary = await ChatbotService.calculateVat(this.candidateId);
      } catch (err) {
        console.error('Error loading VAT summary:', err);
        this.vatSummary = null;
        this.vatSummaryError = 'Failed to load VAT summary';
      } finally {
        this.vatSummaryLoading = false;
      }
    },
    formatSummaryValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return new Intl.NumberFormat('en-AE', {
          style: 'currency',
          currency: 'AED',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numericValue);
      }

      return String(value);
    },
    formatFilingPeriod(start, end) {
      if (!start || !end) {
        return 'N/A';
      }

      const startDate = new Date(start);
      const endDate = new Date(end);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return `${start} – ${end}`;
      }

      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return `${startDate.toLocaleDateString('en-US', options)} – ${endDate.toLocaleDateString('en-US', options)}`;
    },
    async loadDocuments() {
      this.loading = true;
      this.error = null;
      try {
        const response = await ChatbotService.getUploads(this.candidateId, {
          type: 'vat_statement',
          page: 1,
          page_size: 100
        });
        const docs = Array.isArray(response) ? response : (response.data || response.files || []);
        this.documents = docs.map(d => ({
          ...d,
          status: d.scan_status || d.status || 'pending'
        }));
      } catch (err) {
        console.error('Error loading VAT documents:', err);
        this.error = 'Failed to load VAT documents';
      } finally {
        this.loading = false;
      }
    },
    uploadDocument() {
      this.$router.push({
        name: 'chatbot-upload',
        params: { candidateId: this.candidateId },
        query: { type: 'vat_statement', file_type: 'vat_statement' }
      });
    },
    viewDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: { candidateId: this.candidateId, fileId }
        });
      }
    },
    async deleteDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (!fileId) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${doc.name || doc.file_name || 'this document'}"?`,
        { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;
      try {
        await ChatbotService.deleteFile(fileId);
        this.$bvToast.toast('Document deleted successfully', { title: 'Success', variant: 'success', solid: true });
        await this.loadDocuments();
      } catch (err) {
        this.$bvToast.toast('Failed to delete document.', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    isDocumentSelected(docId) {
      return this.selectedDocuments.some(d => d.id === docId);
    },
    toggleDocumentSelection(doc) {
      const index = this.selectedDocuments.findIndex(d => d.id === doc.id);
      if (index > -1) {
        this.selectedDocuments.splice(index, 1);
      } else {
        this.selectedDocuments.push(doc);
      }
    },
    clearSelection() {
      this.selectedDocuments = [];
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedDocuments = [...this.documents];
      } else {
        this.selectedDocuments = [];
      }
    },
    async deleteSelectedDocuments() {
      if (this.selectedDocuments.length === 0) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete ${this.selectedDocuments.length} file${this.selectedDocuments.length !== 1 ? 's' : ''}? This action cannot be undone.`,
        { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;
      this.deletingFiles = true;
      const toDelete = [...this.selectedDocuments].filter(d => d.id || d.file_id);
      try {
        await Promise.all(toDelete.map(d => ChatbotService.deleteFile(d.id || d.file_id)));
        this.$bvToast.toast(`${toDelete.length} file${toDelete.length !== 1 ? 's' : ''} deleted successfully`, { title: 'Success', variant: 'success', solid: true });
        this.selectedDocuments = [];
        await this.loadDocuments();
      } catch (err) {
        this.$bvToast.toast('Failed to delete documents.', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deletingFiles = false;
      }
    },
    getStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },
    getStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing' || status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatName(name) {
      if (!name) return '';
      const parts = name.split('.');
      return parts.length > 1 ? parts.slice(0, -1).join('.') : name;
    },
    getFileIcon(doc) {
      const documentType = doc.document_type || doc.type;
      const ext = (doc.name || doc.file_name || '').split('.').pop()?.toLowerCase();

      if (documentType) {
        switch (documentType) {
          case 'application/pdf':
          case 'pdf':
            return 'las la-file-pdf';
          case 'application/vnd.ms-excel':
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          case 'excel':
          case 'xlsx':
          case 'xls':
            return 'las la-file-excel';
          case 'image/jpeg':
          case 'image/png':
            return 'las la-portrait';
        }
      }
      switch (ext) {
        case 'pdf': return 'las la-file-pdf';
        case 'xlsx':
        case 'xls':
          return 'las la-file-excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'las la-portrait';
        default:
          return 'las la-file';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.vat-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.vat-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
}

.loading-state, .error-state {
  text-align: center;
  padding: 3rem 2rem;
}

.error-state i { font-size: 3rem; color: #dc2626; margin-bottom: 1rem; }
.error-state p { color: #dc2626; margin-bottom: 1rem; }

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.vat-sections { display: flex; flex-direction: column; gap: 2rem; }

.vat-summary-section {
  padding: 0;
  background: transparent;
}

.vat-summary-title {
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  font-family: $brand-font-medium;
}

.vat-summary-state {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #64748b;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
}

.vat-summary-error {
  color: #dc2626;
}

.vat-summary-retry {
  border: none;
  background: transparent;
  color: $color-primary;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.vat-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.vat-summary-card {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;

  &.highlight {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
}

.vat-summary-label {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-family: $brand-font-medium;
}

.vat-summary-value {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;
}

.vat-summary-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.vat-data-table-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.vat-data-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
  font-family: $brand-font-medium;
}

.vat-data-table-scroll {
  overflow-x: auto;
}

.vat-data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    text-align: right;
    font-size: 0.875rem;
    font-family: $brand-font-regular;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  thead th {
    background: #f8fafc;
    color: #64748b;
    font-family: $brand-font-medium;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  tbody td {
    color: #1e293b;
    font-variant-numeric: tabular-nums;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.vat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.vat-section-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;

  .status-legend-item {
    display: inline-flex;
    align-items: center;
    font-size: 0.8125rem;
    color: #4a5568;
    font-family: $brand-font-regular;

    .status-badge {
      width: 22px;
      height: 22px;
      font-size: 0.9rem;

      i {
        font-size: 1rem;
      }
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-family: $brand-font-medium;
}

.upload-section-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: auto;
  padding: 0.625rem 1rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  font-family: $brand-font-medium;
}

.empty-document {
  padding: 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
  color: #6c757d;
  i { font-size: 3rem; display: block; margin-bottom: 0.5rem; }
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.section-header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
}

.select-all-checkbox {
  position: relative;
  top: 4px;
  left: 7px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
    vertical-align: middle;
    background-color: #ffffff;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;

    &:checked {
      background-color: $color-primary;
      border-color: $color-primary;

      &::after {
        content: '✓';
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        font-weight: bold;
        line-height: 1;
      }
    }

    &:hover {
      border-color: $color-primary;
    }
  }

  span {
    font-size: 0.875rem;
    color: #495057;
    font-family: $brand-font-medium;
  }
}

.file-checkbox {
  flex-shrink: 0;
  position: relative;
  top: 4px;
  display: flex;
  align-items: center;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
    vertical-align: middle;
    background-color: #ffffff;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;

    &:checked {
      background-color: $color-primary;
      border-color: $color-primary;

      &::after {
        content: '✓';
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        font-weight: bold;
        line-height: 1;
      }
    }

    &:hover {
      border-color: $color-primary;
    }
  }
}

.document-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;

  &.selected {
    background-color: rgba($color-primary, 0.05);
    border-left: 3px solid $color-primary;
  }
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.document-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;

  i {
    font-size: 1.5rem;
    color: $color-primary;
    opacity: 0.8;
  }
}

.document-name { flex: 1; font-weight: 500; }
.document-date { font-size: 0.875rem; color: #6c757d; }

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;

  &.status-completed {
    color: #16a34a;
    i { color: #16a34a; }
  }

  &.status-processing {
    color: #3b82f6;
    i { color: #3b82f6; }
  }

  &.status-error {
    color: #dc2626;
    i { color: #dc2626; }
  }

  &.status-pending {
    color: #6b7280;
    i { color: #6b7280; }
  }

  i {
    font-size: 1.3rem;
  }
}

.view-btn {
  background: transparent;
  border: none;
  color: #4a5568;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    i { color: darken(#4a5568, 10%); }
  }

  i { font-size: 1.3rem; }
}

.delete-btn {
  background: transparent;
  border: none;
  color: #dc2626;
  padding: 0.1rem;
  margin-right: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    i { color: darken(#dc2626, 10%); }
  }

  i { font-size: 1.3rem; }
}

.bulk-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.5rem;
  background: rgba($color-primary, 0.05);
  border: 1px solid rgba($color-secondary, 0.2);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.bulk-actions-info {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.bulk-actions-buttons {
  position: relative;
  left: 11px;
  display: flex;
  gap: 0.55rem;
  align-items: center;
}

.bulk-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: transparent;
  color: #e53e3e;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: darken(#e53e3e, 15%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 1.25rem;
    &:hover {
      color: darken(#e53e3e, 15%);
    }
  }
}

.bulk-cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: transparent;
  color: #4a5568;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: darken(#4a5568, 15%);
  }

  i {
    font-size: 1.25rem;
  }
}
</style>
